import React from 'react';
import { Link } from 'react-router-dom';
import './GuideComparisonTable.css';

/**
 * The "X vs the alternatives" table that every MotionPace marketing page
 * publishes — the pillar (#546) and the intent-capture guides (#547), plus
 * anything added to the cluster later.
 *
 * WHY IT IS A COMPONENT NOW. The pages each carried their own copy of the same
 * markup: a scroll wrapper, a hint paragraph, a <thead> and a <tbody> map.
 * Two bugs found in QA were properties of that markup rather than of any one
 * page, so five copies meant five fixes and five chances to miss one:
 *
 *   1. On a phone the table read as broken. A cell's height comes from the
 *      tallest cell in its row INCLUDING the columns scrolled off-screen, so a
 *      390px viewport showed two lines of text above 100-400px of blank panel.
 *      The fix is a CSS restructure at <=640px into one card per row (see
 *      GuideComparisonTable.css) driven by the `data-label` attributes this
 *      component writes onto every cell. That only works if every cell reliably
 *      carries its column's header, which is exactly what a shared renderer
 *      guarantees and five hand-written tables did not.
 *
 *   2. Teal row labels meant different things on different pages — two labels on
 *      one guide, one on another, none on a third. The rule now: teal is a LINK
 *      and nothing else. A row with `to` renders its label as a link and gets
 *      the standard affordance; every other label is white, including the label
 *      of the row the page is arguing for. That row is still marked, by its
 *      background tint.
 *
 * GATING: imported only by the gated pages, so it is only ever in the bundle
 * when they are. Nothing ungated may import this file.
 */

/** The hint above the table. Hidden by CSS once the table fits, and in cards. */
export const TABLE_SCROLL_HINT = 'Scroll the table sideways to see every column';

/**
 * A row's first cell: the technique's name, and the card title on a phone.
 *
 * `to` is the ONLY thing that makes a label teal. `sub` is the second-line
 * qualifier a couple of pages use ("Speed through them / adaptive pacing") and
 * stays plain either way, so the link target is unambiguous.
 */
function RowLabel({ row, onLinkClick }) {
  const label = row.to ? (
    <Link className="gcmp-row-link" to={row.to} onClick={onLinkClick}>
      {row.label}
    </Link>
  ) : (
    row.label
  );

  if (!row.sub) return label;
  return (
    <>
      {label}
      <br />
      <span className="gcmp-row-sub">{row.sub}</span>
    </>
  );
}

/**
 * @param {Array<{key: string, header: string}>} columns  First entry is the row
 *   label column; the rest are read off each row by `key`.
 * @param {Array<object>} rows  `{ id, label, sub?, to?, highlight?, ...cells }`.
 * @param {string} ariaLabel  Announced when focus lands in the scroll region.
 * @param {string} testId  data-testid on the <table>.
 * @param {(row) => void} [onRowLinkClick]  Analytics hook for a linked label.
 */
function GuideComparisonTable({ columns, rows, ariaLabel, testId, onRowLinkClick }) {
  const [labelColumn, ...valueColumns] = columns;

  return (
    <>
      {/* Below the table's min-width the wrapper scrolls sideways rather than
          crushing the columns onto a phone; below 640px it is cards instead and
          CSS hides this line, because there is then nothing to scroll. */}
      <p className="gcmp-hint">{TABLE_SCROLL_HINT}</p>
      {/* tabIndex makes the scroll container reachable by keyboard: browsers do
          not focus a scrollable div on their own, so without it the off-screen
          columns are pointer-only (WCAG 2.1.1). The label is what a screen
          reader announces on landing in it. */}
      <div className="gcmp-wrap" tabIndex={0} role="region" aria-label={ariaLabel}>
        <table className="gcmp-table" data-testid={testId}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.highlight ? 'gcmp-row-highlight' : undefined}>
                {/* data-label is what the phone layout prints above each value;
                    on the label cell it is unused but kept for symmetry. */}
                <th scope="row" data-label={labelColumn.header}>
                  <RowLabel
                    row={row}
                    onLinkClick={onRowLinkClick ? () => onRowLinkClick(row) : undefined}
                  />
                </th>
                {valueColumns.map((column) => (
                  <td key={column.key} data-label={column.header}>
                    {row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default GuideComparisonTable;
